/* eslint-disable @typescript-eslint/no-empty-function */
import styled from 'styled-components'
import { DashboardCard, P } from 'styling/GlobalStyles'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { IWidget } from 'types/dashboard.interface'
import { TitleText } from 'styling/Typography'
import { WidgetEditOverlay } from 'pages/Dashboard/Dashboard.styled'
import { HrDashboardInnerWidgetContainer } from '../HrDashboard.styled'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useState } from 'react'
import { getHrDashboardNotifications } from 'features/hrDashboard/hr.slice'
import { AppDispatch, RootState } from 'store'
import { NotificationsModal } from './NotificationsModal'
import { createPortal } from 'react-dom'
import { formatDateForDisplay } from 'utils/helpers'

/** Color per notification type (falls back to orange) */
const TYPE_COLORS: Record<string, string> = {
  birthday: '#FF4F46',
  contract: '#ff9900',
  safety: '#38bdf8',
  default: '#ff9900',
}

function dotColor(type: string): string {
  const key = Object.keys(TYPE_COLORS).find((k) => type?.toLowerCase().includes(k))
  return key ? TYPE_COLORS[key] : TYPE_COLORS.default
}

/* ── Styled ── */

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const ViewMore = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colorOrange};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 3px;
  white-space: nowrap;
`

const NotifList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
`

const NotifItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.thinSeparator};

  &:last-child {
    border-bottom: none;
  }
`

const Dot = styled.span<{ $color: string }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  flex-shrink: 0;
  margin-top: 3px;
`

const NotifBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const NotifMessage = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.textColor};
  line-height: 1.4;
`

const NotifTime = styled.span`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.greyText};
`

const EmptyText = styled.p`
  text-align: center;
  width: 100%;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.greyText};
  padding: 1rem 0;
`

/* ── Component ── */

export const HrNotificationWidget = ({
  modalOpen,
  removeWidget,
  onDragOver,
  onDragStart,
  onDragDrop,
}: IWidget) => {
  const dispatch = useDispatch<AppDispatch>()
  const notifications = useSelector((store: RootState) => store.hrDashboard.notifications)
  const [modal, setModal] = useState(false)

  useEffect(() => {
    dispatch(getHrDashboardNotifications({ page: 1, limit: 5 }))
  }, [])

  return (
    <DashboardCard
      draggable={modalOpen}
      onDragStartCapture={() => onDragStart('notifications', 1)}
      onDrop={onDragDrop}
      onDragEnd={(e) => e.preventDefault()}
      onDragOver={(e) => {
        e.preventDefault()
        onDragOver('notifications')
      }}
      width='1fr'
      style={{ gridArea: 'notifications', cursor: modalOpen ? 'grab' : 'normal' }}
    >
      {modalOpen && (
        <WidgetEditOverlay
          onClick={(e) => {
            removeWidget('notifications')
            e.stopPropagation()
          }}
        >
          <P>Remove Widget</P>
          <CloseIcon />
        </WidgetEditOverlay>
      )}

      <CardHeader>
        <TitleText weight='bold' style={{ margin: 0 }}>
          NOTIFICATIONS
        </TitleText>
        {notifications?.totalCount > 5 && (
          <ViewMore type='button' onClick={() => setModal(true)}>
            View more <span aria-hidden='true'>{'>'}</span>
          </ViewMore>
        )}
      </CardHeader>

      <HrDashboardInnerWidgetContainer padding='0' style={{ height: '100%' }}>
        {notifications.data.length ? (
          <NotifList>
            {notifications.data.map((n) => (
              <NotifItem key={n.id}>
                <Dot $color={dotColor(String(n.type ?? n.message))} />
                <NotifBody>
                  <NotifMessage>{n.message}</NotifMessage>
                  <NotifTime>{formatDateForDisplay(n.createdAt, true)}</NotifTime>
                </NotifBody>
              </NotifItem>
            ))}
          </NotifList>
        ) : (
          <EmptyText>No Notifications Yet</EmptyText>
        )}
      </HrDashboardInnerWidgetContainer>

      {modal && createPortal(<NotificationsModal closeModal={() => setModal(false)} />, document.body)}
    </DashboardCard>
  )
}
